import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Person } from '../../types';
import type {
  CandidateEducationEntry,
  EducationInput,
} from '../../types/candidateDetails';
// @ts-ignore
import { peopleApi } from '../../services/api';
import { useFocusTrap } from '../../hooks/useFocusTrap';

/**
 * Phase 3E-3 (Commit 3 — 3B) — full-screen editor for an external
 * candidate's education collection.
 *
 * Atomic PUT /api/people/:id/education replaces the `admin_edit` shadow
 * layer only; the `cv` layer stays untouched and remains visible via
 * `getSourceLayers()`. `projection_locks.education = true` is stamped
 * server-side so CV re-imports leave manual edits alone.
 *
 * UX:
 *   - Entry reorder via native HTML5 drag-and-drop.
 *   - Unsaved-changes guard on close / Cancel / Escape.
 *   - Field-level 400 / 413 errors parsed from dotted path
 *     `education[i].field` and highlighted on the offending row; cap
 *     errors with `field = "education"` surface as a global banner.
 *
 * Wireframe: docs/designs/candidate-edit-flows.svg (section F).
 */

import { adminProfileApi, type ProfileMutationsApi } from '../../services/profileMutationsApi';

export interface EducationEditorModalProps {
  person: Person;
  apiClient?: ProfileMutationsApi;
  onClose: () => void;
  onSaved: (updated: Person) => void;
}

const DRAG_TYPE = 'application/x-education-reorder';

interface EntryForm {
  localId: string;
  institution: string;
  degree: string;
  field_of_study: string;
  start_date: string;
  end_date: string;
  grade: string;
  summary: string;
}

let _localIdCounter = 0;
function nextLocalId(): string {
  _localIdCounter += 1;
  return `edu-${_localIdCounter}-${Date.now().toString(36)}`;
}

function entryFromCandidate(e: CandidateEducationEntry): EntryForm {
  return {
    localId: nextLocalId(),
    institution: e.institution ?? '',
    degree: e.degree ?? '',
    field_of_study: e.fieldOfStudy ?? '',
    start_date: e.startDate ?? '',
    end_date: e.endDate ?? '',
    grade: e.grade ?? '',
    summary: e.summary ?? '',
  };
}

function blankEntry(): EntryForm {
  return {
    localId: nextLocalId(),
    institution: '',
    degree: '',
    field_of_study: '',
    start_date: '',
    end_date: '',
    grade: '',
    summary: '',
  };
}

function nullIfEmpty(s: string): string | null {
  const t = s.trim();
  return t === '' ? null : t;
}

function entryToPayload(e: EntryForm): EducationInput {
  return {
    institution: nullIfEmpty(e.institution),
    degree: nullIfEmpty(e.degree),
    field_of_study: nullIfEmpty(e.field_of_study),
    start_date: nullIfEmpty(e.start_date),
    end_date: nullIfEmpty(e.end_date),
    grade: nullIfEmpty(e.grade),
    summary: nullIfEmpty(e.summary),
  };
}

interface ParsedError {
  index: number | null;
  leaf: string;
}

/** Parse `education[3].institution` → { index: 3, leaf: "institution" }. */
function parseErrorField(field: string | undefined): ParsedError | null {
  if (!field) return null;
  const m = /^education\[(\d+)\]\.?(.*)$/.exec(field);
  if (!m) return { index: null, leaf: field };
  return { index: Number(m[1]), leaf: m[2] ?? '' };
}

export const DEGREE_OPTIONS = [
  "High School",
  "Associate's Degree",
  "Bachelor's Degree",
  "Master's Degree",
  "Doctorate (PhD)",
  "MBA",
  "Professional Certification",
  "Other"
];

export const EducationEditorModal: React.FC<EducationEditorModalProps> = ({
  person,
  apiClient = adminProfileApi,
  onClose,
  onSaved,
}) => {
  const initialEntries = useMemo<EntryForm[]>(() => {
    const src = person.CandidateDetails?.education ?? [];
    return src.map(entryFromCandidate);
  }, [person]);

  const [entries, setEntries] = useState<EntryForm[]>(initialEntries);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<{ field: string; message: string } | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const draggedIndexRef = useRef<number | null>(null);
  const scrollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const modalTrapRef = useRef<HTMLDivElement>(null);
  useFocusTrap(modalTrapRef, true);
  useEffect(
    () => () => {
      if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
    },
    []
  );

  const parsedError = useMemo(() => parseErrorField(fieldError?.field), [fieldError]);

  const isDirty = useMemo(() => {
    if (entries.length !== initialEntries.length) return true;
    return (
      JSON.stringify(entries.map(entryToPayload)) !==
      JSON.stringify(initialEntries.map(entryToPayload))
    );
  }, [entries, initialEntries]);

  const confirmClose = useCallback(
    (next: () => void) => {
      if (!isDirty) {
        next();
        return;
      }
      const ok = window.confirm(
        'You have unsaved education changes. Discard them and close the editor?'
      );
      if (ok) next();
    },
    [isDirty]
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') confirmClose(onClose);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [confirmClose, onClose]);

  const patchEntry = (idx: number, mut: (e: EntryForm) => EntryForm) => {
    setEntries((prev) => prev.map((e, i) => (i === idx ? mut(e) : e)));
    setFieldError(null);
  };

  const addEntry = () => setEntries((prev) => [...prev, blankEntry()]);

  const removeEntry = (idx: number) => {
    setEntries((prev) => prev.filter((_, i) => i !== idx));
    setFieldError(null);
  };

  const moveEntry = (from: number, to: number) => {
    if (from === to) return;
    setEntries((prev) => {
      const copy = prev.slice();
      const [item] = copy.splice(from, 1);
      copy.splice(to, 0, item);
      return copy;
    });
  };

  const onDragStart = (idx: number) => (e: React.DragEvent<HTMLLIElement>) => {
    draggedIndexRef.current = idx;
    e.dataTransfer.setData(DRAG_TYPE, String(idx));
    e.dataTransfer.effectAllowed = 'move';
  };
  const onDragOver = (idx: number) => (e: React.DragEvent<HTMLLIElement>) => {
    if (
      !Array.from(e.dataTransfer.types).includes(DRAG_TYPE) &&
      draggedIndexRef.current == null
    )
      return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(idx);
  };
  const onDragLeave = () => setDragOverIndex(null);
  const onDrop = (idx: number) => (e: React.DragEvent<HTMLLIElement>) => {
    e.preventDefault();
    const from = draggedIndexRef.current;
    setDragOverIndex(null);
    draggedIndexRef.current = null;
    if (from == null) return;
    moveEntry(from, idx);
  };
  const onDragEnd = () => {
    draggedIndexRef.current = null;
    setDragOverIndex(null);
  };

  const handleSave = async () => {
    if (!person.id || saving) return;
    setSaving(true);
    setServerError(null);
    setFieldError(null);
    try {
      const payload = entries.map(entryToPayload);
      const updated = await apiClient.replaceEducation(person.id, payload);
      onSaved(updated);
    } catch (e: any) {
      const data = e?.response?.data ?? {};
      const message = data.error || e?.message || 'Failed to save education';
      if (typeof data.field === 'string') {
        setFieldError({ field: data.field, message });
        const parsed = parseErrorField(data.field);
        if (parsed?.index != null) {
          if (scrollTimerRef.current) clearTimeout(scrollTimerRef.current);
          scrollTimerRef.current = setTimeout(() => {
            const el = document.querySelector(
              `[data-testid="education-editor-row-${parsed.index}"]`
            );
            if (el && typeof (el as HTMLElement).scrollIntoView === 'function') {
              (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
          }, 50);
        }
      } else {
        setServerError(message);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      ref={modalTrapRef}
      className="fixed inset-0 z-[70] flex flex-col bg-dark-bg"
      data-testid="education-editor-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="education-editor-title"
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Sticky top bar */}
      <div className="flex items-center justify-between gap-4 p-4 border-b border-dark-border bg-dark-card">
        <div className="min-w-0">
          <h2 id="education-editor-title" className="text-lg font-bold text-dark-text">
            Edit education — {person.Name || 'Unknown'}
          </h2>
          <p className="text-xs text-dark-text-secondary mt-0.5">
            {entries.length} {entries.length === 1 ? 'entry' : 'entries'} · stored as{' '}
            <code className="text-amber-200">admin_edit</code> (CV re-imports won't
            overwrite). Drag rows to reorder.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            type="button"
            onClick={() => confirmClose(onClose)}
            disabled={saving}
            className="px-4 py-2 rounded-md bg-dark-bg border border-dark-border text-dark-text-secondary hover:text-dark-text disabled:opacity-50"
            data-testid="education-editor-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !isDirty}
            className="px-4 py-2 rounded-md bg-indigo-600 hover:bg-indigo-500 text-white font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="education-editor-save"
          >
            {saving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto p-6">
        {serverError && (
          <p
            className="mb-4 text-sm text-red-400"
            role="alert"
            data-testid="education-editor-error"
          >
            {serverError}
          </p>
        )}
        {fieldError && parsedError?.index == null && (
          <p
            className="mb-4 text-sm text-red-400"
            role="alert"
            data-testid="education-editor-field-error-global"
          >
            {fieldError.message}
          </p>
        )}

        {entries.length === 0 && (
          <div
            className="text-center py-12 text-dark-text-secondary text-sm border border-dashed border-dark-border rounded-lg"
            data-testid="education-editor-empty"
          >
            No education entries yet. Click "Add entry" to create one.
          </div>
        )}

        <ol className="space-y-4">
          {entries.map((entry, idx) => {
            const rowError =
              parsedError?.index === idx ? fieldError?.message ?? null : null;
            const isDragOver = dragOverIndex === idx;
            return (
              <li
                key={entry.localId}
                draggable
                onDragStart={onDragStart(idx)}
                onDragOver={onDragOver(idx)}
                onDragLeave={onDragLeave}
                onDrop={onDrop(idx)}
                onDragEnd={onDragEnd}
                className={`bg-dark-card border rounded-lg p-4 ${
                  rowError
                    ? 'border-red-500'
                    : isDragOver
                    ? 'border-indigo-400'
                    : 'border-dark-border'
                }`}
                data-testid={`education-editor-row-${idx}`}
              >
                <div className="flex items-center justify-between mb-3 gap-2">
                  <span
                    className="text-xs text-dark-text-secondary cursor-grab select-none"
                    aria-hidden="true"
                  >
                    ⠿ drag
                  </span>
                  <button
                    type="button"
                    onClick={() => removeEntry(idx)}
                    className="text-sm text-red-400 hover:text-red-300"
                    data-testid={`education-editor-row-${idx}-remove`}
                    aria-label="remove education entry"
                  >
                    × Remove
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field
                    label="Institution"
                    testid={`education-editor-row-${idx}-institution`}
                    error={parsedError?.leaf === 'institution' ? rowError : null}
                  >
                    <input
                      type="text"
                      value={entry.institution}
                      onChange={(e) =>
                        patchEntry(idx, (p) => ({ ...p, institution: e.target.value }))
                      }
                      className="w-full px-2 py-1 text-sm rounded bg-dark-bg border border-dark-border text-dark-text"
                    />
                  </Field>
                  <Field
                    label="Degree"
                    testid={`education-editor-row-${idx}-degree`}
                    error={parsedError?.leaf === 'degree' ? rowError : null}
                  >
                    <select
                      value={entry.degree}
                      onChange={(e) =>
                        patchEntry(idx, (p) => ({ ...p, degree: e.target.value }))
                      }
                      className="w-full px-2 py-1 text-sm rounded bg-dark-bg border border-dark-border text-dark-text"
                    >
                      <option value="">Select degree...</option>
                      {DEGREE_OPTIONS.map((opt) => (
                        <option key={opt} value={opt}>
                          {opt}
                        </option>
                      ))}
                      {!DEGREE_OPTIONS.includes(entry.degree) && entry.degree && (
                        <option value={entry.degree}>{entry.degree}</option>
                      )}
                    </select>
                  </Field>
                  <Field
                    label="Field of study"
                    testid={`education-editor-row-${idx}-field_of_study`}
                    error={parsedError?.leaf === 'field_of_study' ? rowError : null}
                  >
                    <input
                      type="text"
                      value={entry.field_of_study}
                      onChange={(e) =>
                        patchEntry(idx, (p) => ({ ...p, field_of_study: e.target.value }))
                      }
                      className="w-full px-2 py-1 text-sm rounded bg-dark-bg border border-dark-border text-dark-text"
                    />
                  </Field>
                  <Field
                    label="Start date (YYYY-MM-DD)"
                    testid={`education-editor-row-${idx}-start_date`}
                    error={parsedError?.leaf === 'start_date' ? rowError : null}
                  >
                    <input
                      type="text"
                      value={entry.start_date}
                      onChange={(e) =>
                        patchEntry(idx, (p) => ({ ...p, start_date: e.target.value }))
                      }
                      placeholder="2019-09-01"
                      className="w-full px-2 py-1 text-sm rounded bg-dark-bg border border-dark-border text-dark-text"
                    />
                  </Field>
                  <Field
                    label="End date (YYYY-MM-DD)"
                    testid={`education-editor-row-${idx}-end_date`}
                    error={parsedError?.leaf === 'end_date' ? rowError : null}
                  >
                    <input
                      type="text"
                      value={entry.end_date}
                      onChange={(e) =>
                        patchEntry(idx, (p) => ({ ...p, end_date: e.target.value }))
                      }
                      placeholder="2023-06-30"
                      className="w-full px-2 py-1 text-sm rounded bg-dark-bg border border-dark-border text-dark-text"
                    />
                  </Field>
                </div>
                <Field
                  label="Summary"
                  testid={`education-editor-row-${idx}-summary`}
                  error={parsedError?.leaf === 'summary' ? rowError : null}
                >
                  <textarea
                    value={entry.summary}
                    onChange={(e) =>
                      patchEntry(idx, (p) => ({ ...p, summary: e.target.value }))
                    }
                    rows={3}
                    className="w-full px-2 py-1 text-sm rounded bg-dark-bg border border-dark-border text-dark-text"
                  />
                </Field>
              </li>
            );
          })}
        </ol>

        <button
          type="button"
          onClick={addEntry}
          className="mt-4 px-3 py-1 text-sm rounded bg-dark-bg border border-dashed border-dark-border text-dark-text-secondary hover:text-dark-text"
          data-testid="education-editor-add"
        >
          + Add entry
        </button>
      </div>
    </div>
  );
};

/**
 * Tiny labeled-field wrapper so the grid stays consistent and we can
 * attach dotted-path error underlines without polluting every field's
 * JSX.
 */
const Field: React.FC<{
  label: string;
  testid: string;
  error: string | null | undefined;
  children: React.ReactNode;
}> = ({ label, testid, error, children }) => (
  <div data-testid={`${testid}-wrap`} className="mt-2 first:mt-0">
    <label className="block text-[11px] font-bold uppercase tracking-wide text-dark-text-secondary mb-1">
      {label}
    </label>
    {children}
    {error && (
      <p
        className="mt-1 text-xs text-red-400"
        role="alert"
        data-testid={`${testid}-error`}
      >
        {error}
      </p>
    )}
  </div>
);
